/**
 * p-muestras.js
 *
 * Lógica de carrito para muestras gratuitas y regalo por umbral.
 * Se carga desde `theme.liquid` (ver "Notas") y se apoya en los
 * data-attributes que pintan los snippets `p-muestras.liquid` y la barra
 * `.p-barra`.
 *
 * Funcionalidades
 * ---------------
 * 1. Selector de muestras (`.p-muestras`)
 *    Intercepta los formularios "Añadir" / "Quitar" del snippet y los
 *    resuelve por AJAX (`/cart/add.js` o `/cart/change.js` con qty 0),
 *    sin recargar ni cerrar el cart drawer. Al terminar relee `/cart.js`
 *    y lanza `cart:update` para que Horizon repinte carrito, drawer y
 *    el propio selector. El límite de muestras (2 ó 4 según umbral) lo
 *    decide el Liquid; aquí solo se ejecuta la acción que ya se muestra.
 *
 * 2. Regalo automático por umbral (`gestionarRegalo`)
 *    Lee `data-regalo-umbral` y `data-regalo-variant-id` de `.p-barra`.
 *    En cada `cartUpdate` calcula el total del carrito SIN contar el
 *    regalo (para que su precio no afecte al umbral) y:
 *      - si no hay regalo y el total >= umbral → lo añade
 *      - si hay regalo y el total < umbral   → lo quita
 *    Solo relanza `cartUpdate` si realmente cambió algo, para no entrar
 *    en bucle. Un flag `procesandoRegalo` evita ejecuciones solapadas
 *    que añadan/quiten dos veces.
 *
 * 3. Limpieza de carrito "solo gratis" (`vaciarCartSiSoloHayProductosGratis`)
 *    En `DOMContentLoaded` y en cada `cartUpdate`, si el carrito tiene
 *    líneas pero el total original es 0 (solo muestras/regalo), lo vacía
 *    con `/cart/clear.js` para que no queden productos gratis huérfanos.
 *
 * Notas
 * -----
 * - Listener delegado en `document`: los snippets se re-renderizan en
 *   cada cambio del carrito, así que no se puede enganchar a los
 *   formularios directamente.
 * - Se carga desde `theme.liquid`, NO desde el snippet: los <script>
 *   inyectados vía innerHTML al repintar una sección no se ejecutan.
 * - El botón de muestras no se reactiva tras el éxito a propósito: el
 *   snippet se repinta entero y ese botón deja de existir.
 * - Pendiente: revisar qué pasa con muestras ya en carrito si se
 *   desactiva la opción desde el admin (¿se quitan solas?).
 */
import { ThemeEvents } from '@theme/events';
const $ = (selector) => document.querySelector(selector);

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('.p-muestras form');
  if (!form) return;
  event.preventDefault();

  const button = form.querySelector('button');
  if (button.disabled) return;
  button.disabled = true;

  const id = form.querySelector('[name="id"]').value;
  const quantity = Number(form.querySelector('[name="quantity"]').value);
  const url = quantity === 0 ? '/cart/change.js' : '/cart/add.js';
  const body = quantity === 0
    ? { id: id, quantity: 0 }
    : { items: [{ id: Number(id), quantity: quantity }] };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('cart error');

    const cart = await (await fetch('/cart.js')).json();
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'p-muestras',
        data: { source: 'p-muestras', itemCount: cart.item_count },
      },
    }));
  } catch (error) {
    console.error('[p-muestras]', error);
    button.disabled = false;
  }
});
//revisar caso en el que si tenias muestras gratis en car pero de repente descativamos opción que se quiten¿?

async function vaciarCartSiSoloHayProductosGratis() {
  const cart = await fetch(window.Shopify.routes.root + 'cart.js').then(r => r.json());
  if (cart.item_count > 0 && cart.original_total_price === 0) {
    await fetch(window.Shopify.routes.root + 'cart/clear.js', { method: 'POST' });
  }
}


let procesandoRegalo = false;

async function gestionarRegalo() {
  if (procesandoRegalo) return; // avoids two runs overlapping and adding/removing twice
  procesandoRegalo = true;

  try {
    const barra = $('.p-barra');
    if (!barra) return;

    const umbral = Number(barra.dataset.regaloUmbral);
    const idRegalo = Number(barra.dataset.regaloVariantId);
    const root = window.Shopify.routes.root;

    const cart = await fetch(root + 'cart.js').then((r) => r.json());

    const lineaRegalo = cart.items.find((item) => item.variant_id === idRegalo);
    const hayRegalo = Boolean(lineaRegalo);

    // Total WITHOUT the gift, so its own price never affects the threshold
    const totalSinRegalo = cart.items
      .filter((item) => item.variant_id !== idRegalo)
      .reduce((sum, item) => sum + item.final_line_price, 0);

    console.log('umbral:', umbral, '| total sin regalo:', totalSinRegalo, '| hayRegalo:', hayRegalo);

    let cartActualizado = null;

    if (!hayRegalo && totalSinRegalo >= umbral) {
      // ADD
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: idRegalo, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('error adding regalo');
      cartActualizado = await fetch(root + 'cart.js').then((r) => r.json());

    } else if (hayRegalo && totalSinRegalo < umbral) {
      // REMOVE
      const res = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(idRegalo), quantity: 0 }),
      });
      if (!res.ok) throw new Error('error removing regalo');
      cartActualizado = await res.json(); // change.js already returns the updated cart
    }

    // Only repaint if something actually changed (this also prevents loops)
    if (cartActualizado) {
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: {
          resource: cartActualizado,
          sourceId: 'p-muestras',
          data: { source: 'p-muestras', itemCount: cartActualizado.item_count },
        },
      }));
    }
  } catch (error) {
    console.error('[regalo]', error);
  } finally {
    procesandoRegalo = false;
  }
}

document.addEventListener('DOMContentLoaded', vaciarCartSiSoloHayProductosGratis);
document.addEventListener(ThemeEvents.cartUpdate, vaciarCartSiSoloHayProductosGratis);
document.addEventListener(ThemeEvents.cartUpdate, gestionarRegalo);
